import { DefaultLogger, LogMessageType } from './Logger';

/**
 * Parses the URL parameters (provided by the manifest.xml file).
 * It acts as the entry point to the component.
 *
 * @param paramName the name of the parameter to be retrieved
 * @returns the first url parameter
 */
export function getUrlParameter(paramName: string): string {
    if (typeof window === 'undefined') return '';
    paramName = paramName.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    let regex = new RegExp('[\\?&]' + paramName + '=([^&#?]*)');
    let results = regex.exec(location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

/**
 * Takes a hex-encoded color and adds the transparency
 *
 * @param hex the color to be processed
 * @param opacity the new color opacity (from 0 - 100)
 */
export function convertHexToRGBA(hex: string, opacity: number) {
    const tempHex = hex.replace('#', '');
    const r = parseInt(tempHex.substring(0, 2), 16);
    const g = parseInt(tempHex.substring(2, 4), 16);
    const b = parseInt(tempHex.substring(4, 6), 16);

    return `rgba(${r},${g},${b},${opacity / 100})`;
};

/**
 * Exception class for all Ge API operations
 */
export class GeException {
    protected errorId: string;
    protected errorLog: string;
    protected options: any;

    constructor(errorId: string, errorLog: string, options?: any) {
        this.errorId = errorId;
        this.errorLog = errorLog;
        this.options = options;
    }

    public getErrorLog() {
        return this.errorLog;
    }

    public getErrorId() {
        return this.errorId;
    }

    public getOptions() {
        return this.options;
    }

    static logException(logger: DefaultLogger, e: any) {
        if (e instanceof GeException) {
            logger.logMessageWithType(LogMessageType.error, e.getErrorLog());
        }
        else if (e instanceof Error) {
            logger.logMessageWithType(LogMessageType.error, e.message);

            // Mainly useful for debugging native clients easier,
            // as otherwise the error will be logged in the console not blocking code execution.
            // Console may not be available in that client at that time, so we throw and stop execution,
            // to see the error as outlook notification.

            // This is safe to deploy, it is commented out in order to
            // only alter the behavior of the addin on demand by the developer.

            // Uncomment to throw all errors in development mode.
            // if (__DEV__) {
            //     throw e;
            // }
        }
        else if (typeof e === 'string') {
            logger.logMessageWithType(LogMessageType.error, e);
        }
        else {
            logger.logMessageWithType(LogMessageType.error, JSON.stringify(e));
        }
    }
}

/**
 * Process the result of a getElementsByTagName() operation and checks the result to see if it's null.
 * If not null, it returns its innerHTML value as string. Otherwise it returns null.
 * @param element the collection of elements that's the result of getElementsByTagName()
 */
export function getTagValue(element: HTMLCollectionOf<Element>) {
    if (element && element.length > 0 && element[0].childNodes[0].nodeValue) {
        return element[0].childNodes[0].nodeValue.trim();
    }

    return null;
}

/**
 * Process the result of a getElementsByTagName() operation and checks the result to see if it's null.
 * If not null, it returns its innerHTML value as string. Otherwise it returns null.
 * @param element the collection of elements that's the result of getElementsByTagName()
 */
export function getTagValueOfElement(element: Element) {
    if (element && element.childNodes[0].nodeValue) {
        return element.childNodes[0].nodeValue.trim();
    }

    return null;
}